import { RootState } from 'store';
import { createSelector } from '@reduxjs/toolkit';
import { convertToDate, isDateLike } from 'utils/dates';
import { isAfter, isBefore } from 'date-fns';
import { groupBy } from 'lodash-es';

const selectDrugsByPharmacyState = (state: RootState) => state.drugsByPharmacy;
export const selectDrugsByPharmacyStatuses = createSelector(
  selectDrugsByPharmacyState,
  ({ isLoading, isMounted }) => {
    return { isLoading, isMounted };
  },
);
export const selectDrugsByPharmacyFilters = createSelector(
  selectDrugsByPharmacyState,
  ({ filters }) => {
    return filters
  },
);
export const selectDrugsByPharmacyData = createSelector(
  selectDrugsByPharmacyState,
  ({ data }) => {
    return data
  },
);

const selectUniqueDrugsAndPharmacies = createSelector(selectDrugsByPharmacyData, (rows) => {

  const setPharmacies = new Set<string>()
  const setDrugs = new Set<string>()

  for (const row of rows) {
    const { pharmacyName, drugName } = row;
    setPharmacies.add(pharmacyName)
    setDrugs.add(drugName)
  }

  return {
    pharmacies: Array.from(setPharmacies),
    drugs: Array.from(setDrugs),
  }

})

// Stable color palette for pharmacies
const PHARMACY_COLORS = [
  '#85c9a2',
  '#93cbe9',
  '#80a0c7',
  '#f8d000',
  '#f9ca88',
  '#9b59b6',
  '#2ecc71',
  '#3498db',
  '#f39c12',
  '#1abc9c',
  '#34495e',
  '#e67e22',
  '#8e44ad',
  '#16a085',
  '#2980b9',
];

export const selectDrugsByPharmacySelectsPharmacyOptions = createSelector(selectUniqueDrugsAndPharmacies, ({ pharmacies }) => {
  return pharmacies.map((pharmacy) => ({
    id: pharmacy,
    title: pharmacy,
  }))
})

// Selector for getting pharmacies with assigned colors
export const selectPharmaciesWithColors = createSelector(selectUniqueDrugsAndPharmacies, ({ pharmacies }) => {
  return pharmacies.map((pharmacy, index) => ({
    name: pharmacy,
    color: PHARMACY_COLORS[index % PHARMACY_COLORS.length], // Use colors cyclically
  }))
})

export const selectDrugsByPharmacySelectsDrugOptions = createSelector(selectUniqueDrugsAndPharmacies, ({ drugs }) => {
  return drugs.map((drug) => ({
    id: drug,
    title: drug,
  }))
})

export const selectDrugsByPharmacyDataFiltered = createSelector(selectDrugsByPharmacyData, selectDrugsByPharmacyFilters, (rows, filters) => {
  const { pharmacyNames, drugNames, dateRange } = filters

  return rows.filter((row) => {
    const result: boolean[] = [];

    const [start, end] = dateRange;

    if (isDateLike(start)) {
      const date = convertToDate(row.entryYearMonth)
      result.push(isAfter(date, convertToDate(start)))
    }

    if (isDateLike(end)) {
      const date = convertToDate(row.entryYearMonth)
      result.push(isBefore(date, convertToDate(end)))
    }

    if (pharmacyNames.length > 0) {
      result.push(pharmacyNames.includes(row.pharmacyName))
    }

    if (drugNames.length > 0) {
      result.push(drugNames.includes(row.drugName))
    }

    return result.every(Boolean)
  })
})

export const selectDrugsByPharmacyDataPopulatDrugs = createSelector(selectDrugsByPharmacyDataFiltered, (rows) => {
  const groups = groupBy(rows, 'drugName');

  const drugs = Object.entries(groups).map(([drugName, rows]) => {
    return {
      name: drugName,
      totalSent: rows.reduce((acc, row) => acc + row.totalSent, 0),
    }
  })

  return drugs.sort((a, b) => b.totalSent - a.totalSent);
})
export const selectDrugsByPharmacyDataPopulatDosageForms = createSelector(selectDrugsByPharmacyDataFiltered, (rows) => {
  const groups = groupBy(rows, 'dosageFormQty');

  const dosageForms = Object.entries(groups).map(([dosageForm, rows]) => {
    return {
      name: dosageForm,
      totalSent: rows.reduce((acc, row) => acc + row.totalSent, 0),
    }
  })

  return dosageForms.sort((a, b) => b.totalSent - a.totalSent);
})